import { Col, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { GameAlgorithm } from '../../../components/game-algorithm';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  giveItem as teamGiveItem,
  giveScore as teamGiveScore,
  giveTitle as teamGiveTitle,
  navigateToWorldMap as teamNavigateToWorldMap,
  navigateToZone as teamNavigateToZone,
  showArea as teamShowArea,
  openTask as teamOpenTask,
  showTask as teamShowTask,
  pauseTimer as teamPauseTimer,
  startTimer as teamStartTimer,
  LockAsync
} from '../../../services/teams';
import {
  NotificationContent,
  NotificationContext
} from '../../../contexts/notification';
import { getTeamList } from '../../../services/teams';
import { GameContext } from '../../../contexts/game';
import { TeamResponse } from '../../../types/responses/team-response';
import { useParams } from 'react-router-dom';
import { postBroadcastGameStateAsync } from '../../../services/games';
import { useToggle } from 'usehooks-ts';

interface AllTeamFacilitatorStateProps {
  isDisableConfirm?: boolean;
}

interface AlgorithmState {
  operation?: string;
  argument?: string | string[];
  quantity?: number;
}

export const AllTeamFacilitatorState = ({
  isDisableConfirm = false
}: AllTeamFacilitatorStateProps) => {
  const { gameCode } = useParams();
  const [game] = useContext(GameContext);
  const [notification, setNotification] = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useToggle(false);
  const [algorithmState, setAlgorithmState] = useState<AlgorithmState>({});

  const teams = useMemo(
    () => game.gameState?.teams ?? [],
    [game.gameState?.teams]
  );

  const isAllLocked = useMemo(
    () => teams.map((t) => t.isLocked).every((t) => t),
    [teams]
  );

  const lockUnlockAllTeams = useCallback(async () => {
    if (!gameCode) return;

    setIsLoading();

    const process = teams.map((t) =>
      LockAsync(gameCode, t.code ?? '', !isAllLocked)
    );

    await Promise.all(process);
    await postBroadcastGameStateAsync(gameCode);

    setIsLoading();
  }, [teams, isAllLocked, gameCode]);

  const responseTeamNotification = (
    message: string,
    isError: boolean = false
  ) => {
    const responseTeamNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(responseTeamNotification);
    setNotification({ ...notification, content });
  };

  const onConfirmHandler = async () => {
    for (const team of teams) {
      const code = team.code ?? '';

      switch (algorithmState?.operation) {
        case 'navigateToWorldMap':
          await teamNavigateToWorldMap(game.gameCode!, code!);
          break;
        case 'navigateToZone':
          await teamNavigateToZone(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string
          );
          break;
        case 'openTask':
          await teamOpenTask(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string
          );
          break;
        case 'showTask':
          await teamShowTask(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            true
          );
          break;
        case 'hideTask':
          await teamShowTask(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            false
          );
          break;
        case 'showArea':
          await teamShowArea(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            true
          );
          break;
        case 'hideArea':
          await teamShowArea(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            false
          );
          break;
        case 'startTimer':
          await teamStartTimer(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            true
          );
          break;
        case 'stopTimer':
          await teamStartTimer(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            false
          );
          break;
        case 'pauseTimer':
          await teamPauseTimer(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            true
          );
          break;
        case 'resumeTimer':
          await teamPauseTimer(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            false
          );
          break;
        case 'giveItem':
          await teamGiveItem(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            algorithmState?.quantity!,
            true
          );
          break;
        case 'removeItem':
          await teamGiveItem(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            algorithmState?.quantity! * -1,
            false
          );
          break;
        case 'giveScore':
          await teamGiveScore(
            game.gameCode!,
            code!,
            algorithmState?.quantity!,
            true
          );
          break;
        case 'removeScore':
          await teamGiveScore(
            game.gameCode!,
            code!,
            algorithmState?.quantity! * -1,
            false
          );
          break;
        case 'giveTitle':
          await teamGiveTitle(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            true
          );
          break;
        case 'removeTitle':
          await teamGiveTitle(
            game.gameCode!,
            code!,
            algorithmState?.argument! as string,
            false
          );
          break;
        default:
          break;
      }
    }

    responseTeamNotification('Successfully sent the command');
  };

  const onChangeOperationHandler = (operation?: string) => {
    setAlgorithmState({ ...algorithmState, operation: operation });
  };

  const onChangeArgumentHandler = (selectedArguments?: string | string[]) => {
    setAlgorithmState({ ...algorithmState, argument: selectedArguments });
  };

  const onChangeQuantityHandler = (quantity?: number) => {
    setAlgorithmState({ ...algorithmState, quantity: quantity });
  };

  return (
    <Row className={'d-flex flex-column gap-4'}>
      <Col md={{ span: 12 }} className={'pl-1'}>
        <hr className={'ml-2'} />
        <div>
          <label className={'pl-2 text-secondary'}>Manual Action</label>
          <div
            className={
              'd-flex justify-content-between align-items-center w-100'
            }>
            <div>
              <GameAlgorithm
                onChangeOperation={onChangeOperationHandler}
                onChangeArgument={onChangeArgumentHandler}
                onChangeQuantity={onChangeQuantityHandler}
                disabled={isDisableConfirm}
              />
            </div>
            <Button
              themeColor={'success'}
              className={'w-8 ml-2'}
              onClick={onConfirmHandler}
              disabled={isDisableConfirm}>
              Confirm
            </Button>
          </div>
        </div>
        <div className={'pl-2'}>
          <Button
            disabled={isLoading}
            onClick={lockUnlockAllTeams}
            themeColor={'error'}
            fillMode={'outline'}>
            <span className={'d-flex align-items-center gap-1'}>
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: '20px'
                }}>
                lock
              </span>
              {isAllLocked ? 'Unlock all teams' : 'Lock all teams'}
            </span>
          </Button>
        </div>
      </Col>
    </Row>
  );
};
